import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteComponent, Spinner } from '../../../Components';
import { ButtonBase } from '@material-ui/core';
import { useTitle } from '../../../Hooks';
import {
  bottomBoxComponentUpdate,
  sideMenuIsOpenUpdate,
  sideMenuComponentUpdate,
  showError,
  returnPropsByPermissions,
} from '../../../Helper';
import {
  GetFilteredVendorClientTrackingsAPI,
  GetHoldingCompanyNames,
} from '../../../Services';
import { ClientsTrackingTableComponent } from './ClientsTrackingUtilities/Sections';
import ExportButton from '../VendorsView/VendorsTabsSections/Offers/components/ExportButton';
import { ClientsTrackingPermissions } from '../../../Permissions';
import { useKeyGenerator } from '../../../Hooks/keyGenerator';
import { useDebouncedAction } from '../../../Hooks/DebouncedAction';

const parentTranslationPath = 'ClientsTracking';
const translationPath = '';
const defaultFilter = {
  pageIndex: 0,
  pageSize: parseInt(localStorage.getItem('Pagination')) || 25,
  vendorClientTrackingId: null,
  vendorOfferId: null,
  vendorOfferName: null,
  vendorId: null,
  vendorName: null,
  contactId: null,
  contactName: null,
  serviceId: null,
  serviceName: null,
  psiCommissionStatusId: null,
  psiCommissionStatusName: null,
  qrCode: null,
  promoCode: null,
  discountPercentage: null,
  psiCommissionPercentage: null,
  invoiceId: null,
  transactionStatusId: null,
  transactionStatusName: null,
  clientFeedback: null,
  transactionAmount: null,
  vendorServiceCategoryId: null,
  vendorServiceCategoryName: null,
  createdOn: null,
  createdBy: null,
  searchType: null,
  filterBy: null,
  orderBy: 1,
  receipts: null,
};

export const ClientTrackingView = () => {
  const { t } = useTranslation(parentTranslationPath);
  useTitle(t(`clients-tracking`));
  const [isLoading, setIsLoading] = useState(false);
  const [isClearFiltersClicked, setIsClearFiltersClicked] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [filter, setFilter] = useState(defaultFilter);
  const [isFirst, setIsFirst] = useState(false);
  const [key, refresh] = useKeyGenerator();
  const [detailsClientTrackingList, setDetailsClientTrackingList] = useState({
    result: [],
    totalCount: 0,
  });
  const [clientTrackingTableFilter, setClientTrackingTableFilter] =
    useState(null);
  const [data, setData] = useState({
    companyNames: [],
  });

  const getCompanyNames = useDebouncedAction(async (value) => {
    try {
      const res = await GetHoldingCompanyNames({
        search: value,
        pageIndex: 1,
        pageSize: 100,
      });
      setData((item) => ({ ...item, companyNames: res.result }));
    } catch {
      setData((item) => ({ ...item, companyNames: [] }));
      showError('get company names error');
    }
  });

  const onClearedAllFiltersClick = useCallback(() => {
    setIsClearFiltersClicked(isClearFiltersClicked ? false : true);
    setFilter({ ...defaultFilter });
    setClientTrackingTableFilter(null);
    refresh();
  }, []);

  const GetAllClientsTracking = useCallback(async () => {
    if (
      returnPropsByPermissions(
        ClientsTrackingPermissions.ViewClientTracking.permissionsId
      )
    ) {
      setIsLoading(true);
      if (!isFirst) setIsFirst(true);
      if (isLoading) return;

      try {
        const res = await GetFilteredVendorClientTrackingsAPI({
          ...filter,
          pageSize: filter.pageSize,
          pageIndex: filter.pageIndex + 1,
          filterBy:
            (sortBy?.filterBy &&
              sortBy.filterBy[0].toLowerCase() + sortBy.filterBy.slice(1)) ||
            filter?.filterBy ||
            'CreatedOn',
          orderBy: sortBy?.orderBy || filter?.orderBy || 2,
        });
        if (res && res.result) {
          setDetailsClientTrackingList({
            result: res.result,
            totalCount: res.totalCount,
          });
        }
      } catch (error) {
        showError(t`something_went_wrong`);
        setDetailsClientTrackingList({
          result: [],
          totalCount: 0,
        });
      } finally {
        setIsLoading(false);
      }
    }
  }, [filter, sortBy, isLoading]);

  const reloadData = useCallback(() => {
    setFilter((item) => ({ ...item, pageIndex: 0 }));
  }, []);

  useEffect(() => {
    GetAllClientsTracking();
  }, [filter, sortBy]);

  useEffect(
    () => () => {
      bottomBoxComponentUpdate(null);
      sideMenuComponentUpdate(null);
      sideMenuIsOpenUpdate(false);
    },
    []
  );

  useEffect(() => {
    getCompanyNames();
  }, []);

  return (
    <div
      className='d-flex-column'
      key={key}
    >
      <div className='header-section'>
        <div className=''>
          <div className='d-flex d-inline d-flex-v-center-h-between mx-3'>
            <AutocompleteComponent
              idRef='CompanyNameRef'
              inputPlaceholder='select company name'
              multiple={false}
              data={data.companyNames}
              inputClasses='fields-content'
              wrapperClasses='w-auto'
              displayLabel={(option) => option}
              onChange={(_, newValue) => {
                setFilter((item) => ({
                  ...item,
                  companyName: newValue,
                }));
              }}
              withLoader
              withoutSearchButton
              onOpen={() => {
                if (data?.companyNames?.length === 0) getCompanyNames();
              }}
              onInputChange={(_e, value) => getCompanyNames(value)}
            />

            <div className='mt-1  mb-2'>
              <div className='px-2 mt-2 mb-2'>
                <ExportButton
                  filter={{
                    ...filter,
                    pageSize: detailsClientTrackingList?.totalCount,
                    pageIndex: 1,
                    isClientTracking: true,
                    totalCount: detailsClientTrackingList?.totalCount,
                  }}
                />
              </div>

              <ButtonBase
                className='btns theme-solid bg-danger clear-all-btn'
                id='action_apply'
                onClick={onClearedAllFiltersClick}
              >
                <span className='mdi mdi-filter-remove m-1' />
                {t(`clear-filters`)}
              </ButtonBase>
            </div>
          </div>
        </div>
      </div>

      <div className='view-wrapper client-tracking-view'>
        <Spinner
          isActive={isLoading}
          isAbsolute
        />

        <ClientsTrackingTableComponent
          parentTranslationPath={parentTranslationPath}
          detailsClientTrackingList={detailsClientTrackingList}
          filter={filter}
          setFilter={setFilter}
          setSortBy={setSortBy}
          sortBy={sortBy}
          reloadData={reloadData}
          setClientTrackingTableFilter={setClientTrackingTableFilter}
          clientTrackingTableFilter={clientTrackingTableFilter}
          isClearFiltersClicked={isClearFiltersClicked}
          setIsClearFiltersClicked={setIsClearFiltersClicked}
          isFirst={isFirst}
          translationPath={translationPath}
        />
      </div>
    </div>
  );
};
